import { z } from 'zod';
import { ApiClient } from '@/common/api/ApiClient';
import {
  orgMemberSchema,
  organizationSchema,
  type CreateOrganizationRequest,
  type InviteMemberRequest,
  type OrgMember,
  type OrgRole,
  type Organization,
  type UpdateOrganizationRequest,
} from './schemas';

// ─── Mock Data ────────────────────────────────────────────────────────────────

export const mockOrganizations: Organization[] = [
  {
    id: 1,
    name: 'Tech Solutions s.r.o.',
    street: 'Václavské náměstí 1',
    city: 'Prague',
    zipCode: '110 00',
    country: 'CZ',
  },
  {
    id: 2,
    name: 'Green Energy Partners',
    street: 'Náměstí Svobody 10',
    city: 'Brno',
    zipCode: '602 00',
    country: 'CZ',
  },
];

export const mockOrgMembers: Record<number, OrgMember[]> = {
  1: [
    {
      id: 1,
      partner: 1,
      user: { email: 'jan.novak@techsol.cz', firstName: 'Jan', lastName: 'Novák' },
      role: 'full',
    },
    {
      id: 2,
      partner: 1,
      user: { email: 'petra.svobodova@techsol.cz', firstName: 'Petra', lastName: 'Svobodová' },
      role: 'write',
    },
    {
      id: 3,
      partner: 1,
      user: { email: 'tomas.kovar@techsol.cz', firstName: 'Tomáš', lastName: 'Kovář' },
      role: 'read',
    },
    {
      id: 4,
      partner: 1,
      user: { email: 'marie.horackova@extern.cz', firstName: 'Marie', lastName: 'Horáčková' },
      role: 'read',
    },
  ],
  2: [
    {
      id: 1,
      partner: 2,
      user: { email: 'jan.novak@greenep.eu', firstName: 'Jan', lastName: 'Novák' },
      role: 'full',
    },
    {
      id: 5,
      partner: 2,
      user: { email: 'lukas.prochazka@greenep.eu', firstName: 'Lukáš', lastName: 'Procházka' },
      role: 'write',
    },
  ],
};

let nextOrgId = 3;
let nextAccessId = 10;

function findOrg(id: number): Organization {
  const org = mockOrganizations.find((o) => o.id === id);
  if (!org) throw new Error(`Organization ${id} not found`);
  return org;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const OrganizationService = {
  getList: async () => {
    return ApiClient.get('/organizations/', z.array(organizationSchema), undefined, {
      mock: () => mockOrganizations.map((o) => ({ ...o })),
    });
  },

  getById: async (id: number) => {
    return ApiClient.get(`/organizations/${id}`, organizationSchema, undefined, {
      mock: () => ({ ...findOrg(id) }),
    });
  },

  create: async (data: CreateOrganizationRequest, currentUserEmail: string) => {
    return ApiClient.post('/organizations/', data, organizationSchema, {
      mock: () => {
        const orgId = nextOrgId++;
        const newOrg: Organization = {
          id: orgId,
          name: data.name,
          street: data.street,
          city: data.city,
          zipCode: data.zipCode,
          country: data.country,
        };
        mockOrganizations.push(newOrg);
        mockOrgMembers[orgId] = [
          {
            id: nextAccessId++,
            partner: orgId,
            user: {
              email: currentUserEmail,
              firstName: currentUserEmail.split('@')[0],
              lastName: '',
            },
            role: 'full',
          },
        ];
        return { ...newOrg };
      },
    });
  },

  update: async (id: number, data: UpdateOrganizationRequest) => {
    return ApiClient.patch(`/organizations/${id}`, data, organizationSchema, {
      mock: () => {
        const idx = mockOrganizations.findIndex((o) => o.id === id);
        if (idx === -1) throw new Error(`Organization ${id} not found`);
        mockOrganizations[idx] = { ...mockOrganizations[idx], ...data };
        return { ...mockOrganizations[idx] };
      },
    });
  },

  delete: async (id: number) => {
    return ApiClient.delete(`/organizations/${id}`, undefined, z.null(), {
      mock: () => {
        const idx = mockOrganizations.findIndex((o) => o.id === id);
        if (idx !== -1) mockOrganizations.splice(idx, 1);
        return null;
      },
    });
  },

  getMembersList: async () => {
    return ApiClient.get('/organizations/access/', z.array(orgMemberSchema), undefined, {
      mock: () =>
        Object.values(mockOrgMembers).flatMap((members) => members.map((m) => ({ ...m }))),
    });
  },

  inviteMember: async (orgId: number, data: InviteMemberRequest) => {
    return ApiClient.post(
      `/organizations/${orgId}/access`,
      // The partner id is redundant with the URL, so it is set here rather than in the form.
      { ...data, partner: orgId },
      orgMemberSchema,
      {
        mock: () => {
          const members = mockOrgMembers[orgId] ?? (mockOrgMembers[orgId] = []);
          const newMember: OrgMember = {
            id: nextAccessId++,
            partner: orgId,
            user: { email: data.user, firstName: '', lastName: '' },
            role: data.role,
          };
          members.push(newMember);
          return { ...newMember };
        },
      }
    );
  },

  updateMemberRole: async (orgId: number, memberId: number, role: OrgRole) => {
    return ApiClient.patch(`/organizations/access/${memberId}`, { role }, orgMemberSchema, {
      mock: () => {
        const members = mockOrgMembers[orgId] ?? [];
        const memberIdx = members.findIndex((m) => m.id === memberId);
        if (memberIdx === -1) throw new Error(`Member ${memberId} not found`);
        members[memberIdx] = { ...members[memberIdx], role };
        return { ...members[memberIdx] };
      },
    });
  },

  removeMember: async (orgId: number, memberId: number) => {
    return ApiClient.delete(`/organizations/access/${memberId}`, undefined, z.null(), {
      mock: () => {
        const members = mockOrgMembers[orgId] ?? [];
        const idx = members.findIndex((m) => m.id === memberId);
        if (idx !== -1) members.splice(idx, 1);
        return null;
      },
    });
  },
};
